/* global Core, Sys */
Sys.ns("Core");

/**
 * The Money Manager controller.
 *
 * @class Core.MoneyManagerController
 * @extends Core.Controller
 */
Core.MoneyManagerController = {

    /** @inheritdoc */
    constructor: function() {
        Core.MoneyManagerController.superclass.constructor.apply(this, arguments);
    },

    /** @inheritdoc */
    setupEvents: function() {
        this.on({
            "notify:stateHandler.leavingBeforeLoaderCloseState": this.onBeforeLoaderClose,
            "notify:responseParser.responseParsed": this.onServerResponse,
            "notify:settingsManager.settingChanged": this.onSettingChanged,
            "request:moneyManager.revertBet": this.revertBet,

            "notify:freeRounds.reInitResponseReceived": this.onFreeRoundsReInit,
            "notify:freeRounds.ended": this.onFreeRoundsEnded
        });
    },

    /**
     * Checks if we should change betLevel and coinValue and notifies any changes in balance.
     *
     * @private
     * @returns {void}
     */
    onBeforeLoaderClose: function() {
        this.notifyBalanceChange();
    },

    /**
     * Handles server response when parsed.
     *
     * @private
     * @param {Object} serverResponse Parsed server response.
     * @returns {void}
     */
    onServerResponse: function(serverResponse) {
        if (typeof serverResponse.credit !== "undefined") {
            this.model.setBalance(serverResponse.credit);
        }
    },

    /**
     * Handles change in bet level and/or denomination settings.
     *
     * @private
     * @param {string} setting The name of the changed setting.
     * @param {*} value The new value of the setting.
     * @returns {void}
     */
    onSettingChanged: function(setting, value) {
        if (setting === "betLevel") {
            if (this.model.setBetLevel(value)) {
                this.notifyBetChange();
            }
        }
        else if (setting === "denomination") {
            if (this.model.setDenomination(value)) {
                this.notifyBetChange();
            }
        }
    },

    /**
     * Notifies of balance having changed.
     *
     * @private
     * @returns {void}
     */
    notifyBalanceChange: function() {
        this.fireEvent("notify:moneyManager.balanceChanged", {
            denomination: this.model.readData("denomination"),
            playerBetCents: this.model.readData("playerBetCents"),
            playerBalanceCoins: this.model.readData("playerBalanceCoins"),
            playerBalanceCents: this.model.readData("playerBalanceCents"),
            playerBalanceFormatted: this.formatMoneyCurrencySign(this.model.readData("playerBalanceCents"))
        });
    },

    /**
     * Notifies of bet having changed.
     *
     * @private
     * @returns {void}
     */
    notifyBetChange: function() {
        this.fireEvent("notify:moneyManager.betChanged", {
            playerBetCoins: this.model.readData("playerBetCoins"),
            playerBetCents: this.model.readData("playerBetCents"),
            playerBetFormatted: this.formatMoneyCurrencySign(this.model.readData("playerBetCents"))
        });
    },

    /**
     * Cancel the bet by re-adding the bet amount to the credit.
     *
     * @returns {void}
     */
    revertBet: function() {
        this.setBalance(this.getBalanceCents() + this.getBetCents());
        this.reloadBalance();
    },

    /**
     * Sets the player's balance.
     *
     * @param {number} cents The player's balance in cents.
     * @returns {void}
     */
    setBalance: function(cents) {
        if (this.model.setBalance(cents)) {
            this.notifyBetChange();
            this.notifyBalanceChange();
        }
    },

    /**
     * Reloads the balance.
     *
     * @returns {Sys.Deferred} The promise to handle.
     */
    reloadBalance: function() {
        var me = this,
            deferred = new Sys.Deferred(),
            removeListeners = function() {
                me.removeListener("notify:responseParser.reloadBalanceParsed");
                me.removeListener("notify:serverManager.reloadBalanceRequestFailed");
            };

        this.addListener("notify:responseParser.reloadBalanceParsed", function(response) {
            me.setBalance(parseInt(response.credit, 10));
            deferred.resolveWith([response]);

            removeListeners();
            me.fireEvent("notify:moneyManager.balanceReloaded");
        });

        this.addListener("notify:serverManager.reloadBalanceRequestFailed", function(response) {
            deferred.reject(response);

            removeListeners();
        });

        this.fireEvent("request:serverManager.sendAction", "reloadbalance", "notify:responseParser.reloadBalanceParsed", "notify:serverManager.reloadBalanceRequestFailed");

        return deferred;
    },

    /**
     * Returns the formatted value.
     *
     * @param {number} cents The value to format in cents.
     * @param {string} [format="player"] The (optional) currency format to use (player|jackpot).
     * @param {string} [decimal=2] Number of decimal digits.
     * @returns {string} The formatted value as a string.
     */
    formatMoney: function(cents, format, decimal) {
        var formatting = this.model.getMoneyFormat(format),
            newDecimal = Sys.isNumber(decimal) ? decimal : 2,
            valueParts = (Math.floor(cents) / 100).toFixed(newDecimal).split("."),
            unformattedNumberPart = valueParts[0],
            unformattedDecimalPart = valueParts[1],
            formattedAmount = unformattedNumberPart
                .split("")
                .reduce(function(acc, value, index, arr) {
                    var result = acc;

                    if (index > 0 && index % 3 === 0) {
                        result = formatting.thousandsDivider + result;
                    }

                    result = arr[arr.length - index - 1] + result;

                    return result;
                }, "");

        return formattedAmount + (unformattedDecimalPart ? formatting.decimalDivider + unformattedDecimalPart : "");
    },

    /**
     * Returns the formatted value with the currency sign.
     *
     * @param {number} cents The value to format in cents.
     * @param {string} [format="player"] The (optional) currency format to use (player|jackpot).
     * @param {string} [decimal=2] Number of decimal digits.
     * @returns {string} The formatted value as a string.
     */
    formatMoneyCurrencySign: function(cents, format, decimal) {
        var formatting = this.model.getMoneyFormat(format),
            currencySign = formatting.currencyChar,
            formattedAmount = this.formatMoney(cents, format, decimal),
            currencyExtraSpace = function(sign) {
                return sign.length > 1 ? " " : "";
            };

        return formatting.isCurrCharAfter ?
            formattedAmount + currencyExtraSpace(currencySign) + currencySign :
            currencySign + currencyExtraSpace(currencySign) + formattedAmount;
    },

    /**
     * Get the total credit balance in cents.
     *
     * @returns {number} The number of cents available in the balance.
     */
    getBalanceCents: function() {
        return this.model.readData("playerBalanceCents");
    },

    /**
     * Get the total credit balance in coins.
     *
     * @returns {number} The number of cents available in the balance.
     */
    getBalanceCoins: function() {
        return this.model.readData("playerBalanceCoins");
    },

    /**
     * Get the bet in cents.
     *
     * @returns {number} The number of cents in the bet.
     */
    getBetCents: function() {
        return this.model.readData("playerBetCents");
    },

    /**
     * Get the bet in coins.
     *
     * @returns {number} The number of coins in the bet.
     */
    getBetCoins: function() {
        return this.model.readData("playerBetCoins");
    },

    /**
     * Get the betline coin value.
     *
     * @returns {number} The value of betline coin.
     */
    getBetlineCoinValue: function() {
        return this.model.readData("betlineCoins");
    },

    /**
     * Check if there is enough money to place the bet.
     *
     * @returns {boolean} Whether or not the there is enough money to place the bet.
     */
    canPlaceAnotherBet: function() {
        return this.getBetCents() <= this.getBalanceCents() || this.model.readData("inFreeRounds");
    },

    /**
     * Place a bet if possible.
     *
     * @returns {void}
     */
    placeBet: function() {
        if (!this.model.readData("inFreeRounds")) {
            this.setBalance(this.getBalanceCents() - this.getBetCents());
        }
    },

    /**
     * Returns the minimum bet the player must place to play a game round.
     *
     * @returns {number} The minimum bet.
     */
    getMinimumBetCents: function() {
        return this.model.readData("minimumBetInCents");
    },

    /**
     * Returns the currency iso name.
     *
     * @param {string} [format="player"] The (optional) currency format to use (player|jackpot).
     * @returns {string} The iso name of the currency.
     */
    getCurrencyIsoName: function(format) {
        return this.model.getMoneyFormat(format).iso;
    },

    /**
     * Refreshes the model upon re-initialisation.
     *
     * @private
     * @returns {void}
     */
    onFreeRoundsReInit: function() {
        this.model.setupData();
    },

    /**
     * Sets free rounds mode to false.
     *
     * @private
     * @returns {void}
     */
    onFreeRoundsEnded: function() {
        this.model.storeData("inFreeRounds", false);
    }
};

Core.MoneyManagerController = Sys.extend(Core.Controller, Core.MoneyManagerController, "Core.MoneyManagerController");
